import { safeGetItem } from "../../../utils/localStorageUtils";

/**
 * A group the logged-in user can access.
 *
 * `GET group/?page_size=10000` is fetched once at login by
 * `Components/utils/dataFetchers.tsx` and cached under `parentGroups`. The
 * backend already scopes it to the caller's tree (`Group.objects.with_access`),
 * so every row here is a group the user may assign a tunnel to — parent accounts
 * and site-level subgroups alike. Shape mirrors the API's `GroupSerializer`.
 */
export interface AccessibleGroup {
  id: number;
  name: string;
  parent: number | null;
  parent_account: boolean;
}

let cachedRaw: string | null | undefined;
let cachedGroups: AccessibleGroup[] = [];

/**
 * Parsed once per distinct `parentGroups` value: the same array comes back
 * until localStorage changes. Callers key per-list indexes on the array (see
 * Troubleshooting's `ancestorsOrSelf`), and re-parsing a 10,000-row list on
 * every call was itself a measurable share of each render.
 */
export const getAccessibleGroups = (): AccessibleGroup[] => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem("parentGroups");
  } catch {
    raw = null;
  }
  if (raw === cachedRaw) return cachedGroups;
  const groups = safeGetItem<AccessibleGroup[]>("parentGroups", []) ?? [];
  cachedGroups = Array.isArray(groups)
    ? groups.filter(
        (group) => group && typeof group.id === "number" && Boolean(group.name)
      )
    : [];
  cachedRaw = raw;
  return cachedGroups;
};

export const sortGroupsForPicker = (
  groups: AccessibleGroup[]
): AccessibleGroup[] =>
  [...groups].sort((a, b) => a.name.localeCompare(b.name));

/**
 * The highest group in the user's accessible tree — the default assignment for a
 * new tunnel (ENG-272).
 *
 * `with_access` returns the user's own group plus its descendants, so the root of
 * that set is the group whose parent is not itself accessible. A parent account
 * wins ties, then name order, so the answer is stable across reloads regardless of
 * the order the API returned rows in.
 */
export const getHighestAccessibleGroup = (
  groups: AccessibleGroup[]
): AccessibleGroup | null => {
  if (groups.length === 0) {
    return null;
  }
  const accessibleIds = new Set(groups.map((group) => group.id));
  const roots = groups.filter(
    (group) => group.parent == null || !accessibleIds.has(group.parent)
  );
  // No root at all means the cached rows are cyclic or partial; fall back to the
  // whole list rather than leaving the field empty.
  const candidates = roots.length > 0 ? roots : groups;
  return [...candidates].sort((a, b) => {
    if (a.parent_account !== b.parent_account) {
      return a.parent_account ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  })[0];
};
